'use client'

import { useEffect, useRef, useState } from 'react'

import Button from '/react4xp/components/prototype/Button'
import HeroGlyphCircle from '/react4xp/components/prototype/icons/HeroGlyphCircle'
import HeroGlyphLine from '/react4xp/components/prototype/icons/HeroGlyphLine'

import type { HeroFrontpageProps } from './types'

// Cell size mirrors the fixed w-[1.6735rem] h-[2.86825rem] classes on .hero-frontpage-glyph below.
const GLYPH_CELL_WIDTH_PX = 26.776
const GLYPH_CELL_HEIGHT_PX = 45.892
// Keep the bottom row from ever being clipped by the field's overflow-hidden — only add
// as many full rows as fit with at least this much clearance above the field's bottom edge.
const GLYPH_FIELD_BOTTOM_MARGIN_PX = 48

function renderTitle(title: string) {
	return title.split(/(\*\*.+?\*\*)/g).map((segment, index) => {
		const match = segment.match(/^\*\*(.+)\*\*$/)
		if (match) {
			return (
				<span key={index} className="text-brand-300">
					{match[1]}
				</span>
			)
		}
		return (
			<span key={index} className="text-pure-white">
				{segment}
			</span>
		)
	})
}

function GlyphCell() {
	const cellRef = useRef<HTMLDivElement>(null)

	// Randomized client-side, after hydration — computing this in SSR/first-client-render
	// would diverge (each pass rolls its own Math.random()) and trip a hydration warning.
	// Set directly on the node (not via React state) so mounting hundreds of cells doesn't
	// also mean hundreds of extra re-renders.
	useEffect(() => {
		const cell = cellRef.current
		if (!cell) return
		cell.style.setProperty('--hero-frontpage-glyph-duration', `${Math.random() * 19 + 1}s`)
		cell.classList.add('is-ready')
	}, [])

	return (
		<div
			ref={cellRef}
			className="hero-frontpage-glyph group pointer-events-auto relative w-[1.6735rem] h-[2.86825rem] shrink-0 overflow-hidden"
		>
			<HeroGlyphCircle className="hero-frontpage-glyph__circle absolute inset-0 w-full h-full" />
			<HeroGlyphLine className="hero-frontpage-glyph__line absolute inset-0 w-full h-full" />
		</div>
	)
}

function GlyphField() {
	const fieldRef = useRef<HTMLDivElement>(null)
	// Starts empty — measured and filled to the exact count once mounted (see below).
	// Seeding this with a guessed count would either overrender on small viewports or
	// underrender on large ones, and correcting it after mount reflows visibly.
	const [cellCount, setCellCount] = useState(0)

	// Measured client-side, after mount — the field's own box (inset-0, nested inside
	// .container so it tracks its width/centering) is the area the tiled glyph pattern fills.
	useEffect(() => {
		const field = fieldRef.current
		if (!field) return

		const fillField = () => {
			const cols = Math.floor(field.clientWidth / GLYPH_CELL_WIDTH_PX)
			const rows = Math.max(
				1,
				Math.floor((field.clientHeight - GLYPH_FIELD_BOTTOM_MARGIN_PX) / GLYPH_CELL_HEIGHT_PX)
			)
			setCellCount(cols * rows)
		}

		fillField()
		const observer = new ResizeObserver(fillField)
		observer.observe(field)
		return () => observer.disconnect()
	}, [])

	return (
		<div
			ref={fieldRef}
			className="pointer-events-none absolute inset-0 -z-10 flex flex-wrap content-start justify-between overflow-hidden lg:inset-x-9"
			aria-hidden="true"
		>
			{Array.from({ length: cellCount }).map((_, index) => (
				<GlyphCell key={index} />
			))}
		</div>
	)
}

export default function HeroFrontpage({ title, intro, primaryCta, secondaryCta, stats = [] }: HeroFrontpageProps) {
	const hasCta = Boolean(primaryCta || secondaryCta)
	const hasStats = stats.length > 0

	const statsBlock = hasStats && (
		<div className="relative z-10 mt-auto shrink-0 pt-6 pb-3 lg:mt-0 lg:w-40 lg:py-16">
			<div className="flex flex-row items-start gap-6 lg:flex-col lg:gap-7">
				{stats.slice(0, 3).map((stat, index) => (
					<div key={index} className={`flex max-w-25 lg:max-w-37.5 flex-col${index === 1 ? ' mx-auto lg:mx-0' : ''}`}>
						<p className="text-lg lg:text-[2.125rem] leading-[1.15] font-semibold text-pure-white">{stat.value}</p>
						<p className="text-sm leading-normal font-normal text-brand-500">{stat.label}</p>
					</div>
				))}
			</div>
		</div>
	)

	return (
		<section className="bg-black-950">
			<div
				className={`container relative z-10 flex min-h-[38.4375rem] flex-col pb-12 pointer-events-none lg:min-h-[41.125rem] lg:flex-row lg:items-end lg:justify-between lg:pt-3 ${hasStats ? '' : 'justify-center'}`}
			>
				<GlyphField />

				<div className={`pt-18 pb-6 lg:py-16 lg:max-w-200 ${hasStats ? 'mt-auto lg:mt-0' : 'my-auto lg:my-0'}`}>
					<h1 className="text-[2.625rem] lg:text-[3.625rem] leading-[1.15] font-medium font-heading">
						{renderTitle(title)}
					</h1>

					{intro && (
						<p className="mt-4 text-2xl lg:text-[2.125rem] leading-[1.2] font-normal text-pure-white">{intro}</p>
					)}

					{hasCta && (
						<div className="pointer-events-auto mt-8 flex w-fit items-center gap-[1.0625rem]">
							{primaryCta && (
								<Button variant="primary" size="lg" href={primaryCta.href} isExternal={primaryCta.isExternal}>
									{primaryCta.label}
								</Button>
							)}
							{secondaryCta && (
								<Button
									variant="secondary-dark"
									size="lg"
									href={secondaryCta.href}
									isExternal={secondaryCta.isExternal}
								>
									{secondaryCta.label}
								</Button>
							)}
						</div>
					)}
				</div>

				{statsBlock}
			</div>
		</section>
	)
}
